import { Fragment } from 'react';
import { useTracking } from 'react-tracking';
import {
  ROUTE_CREATE_CRITERIA,
  ROUTE_DASHBOARD,
  ROUTE_DASHBOARD_WITH_EVENTS,
  ROUTE_DEPRECATED_PORTFOLIO,
  ROUTE_DEPRECATED_PORTFOLIO_EDIT,
  ROUTE_DEPRECATED_PORTFOLIO_EDIT_TXN,
  ROUTE_DEPRECATED_PORTFOLIO_HOME,
  ROUTE_DEPRECATED_PORTFOLIO_RETURNS_REPORT,
  ROUTE_DEPRECATED_PORTFOLIO_SHARE,
  ROUTE_DEPRECATED_PUBLIC_PORTFOLIO,
  ROUTE_DISCOVER_INVESTING_IDEAS,
  ROUTE_DISCOVER_INVESTING_IDEAS_MARKET,
  ROUTE_DISCOVER_SAMPLE_PORTFOLIOS,
  ROUTE_EDIT_EXPLORE_IDEA,
  ROUTE_EDIT_USER_IDEA,
  ROUTE_INVESTING_IDEA_MARKET,
  ROUTE_INVESTING_IDEA_MARKET_INDUSTRY,
  ROUTE_MARKET,
  ROUTE_MARKET_HOME,
  ROUTE_MARKET_SECTOR,
  ROUTE_MARKET_SECTOR_SECONDARY,
  ROUTE_MARKET_SECTOR_SECONDARY_TERTIARY,
  ROUTE_MY_PORTFOLIOS,
  ROUTE_PORTFOLIO,
  ROUTE_PORTFOLIO_LANDING,
  ROUTE_PORTFOLIO_DEMO,
  ROUTE_PORTFOLIO_DEFAULT,
  ROUTE_PORTFOLIO_NEW,
  ROUTE_PORTFOLIO_HOLDINGS,
  ROUTE_PORTFOLIO_ANALYSIS,
  ROUTE_PORTFOLIO_NARRATIVES,
  ROUTE_PORTFOLIO_DIVIDENDS,
  ROUTE_PORTFOLIO_RETURNS,
  ROUTE_WATCHLIST,
  ROUTE_STOCKS,
  ROUTE_STOCKS_HOME,
  ROUTE_STOCKS_MARKET,
  ROUTE_STOCKS_MARKET_INDUSTRY,
  ROUTE_NARRATIVE_COMMUNITY,
  ROUTE_NARRATIVE_COMMUNITY_DETAIL,
  ROUTE_NARRATIVE_COMMUNITY_UPDATE_DETAIL,
  ROUTE_NARRATIVE_COMMUNITY_DETAIL_UPDATES,
  ROUTE_NARRATIVE_COMMUNITY_PROFILE,
} from '@/constants/routes';
import { useTranslation } from '@/hooks/useTranslation';
import { getLocation } from '@/redux/location/selectors';
import { useSelector } from 'react-redux';
import { RouterLink } from '@redux-first-router-link';
import { cn } from '@simplywallst/ui-core/next';

const subRouteTypes = {
  [ROUTE_DASHBOARD]: [ROUTE_DASHBOARD, ROUTE_DASHBOARD_WITH_EVENTS],
  [ROUTE_DISCOVER_INVESTING_IDEAS]: [
    ROUTE_DISCOVER_INVESTING_IDEAS,
    ROUTE_DISCOVER_INVESTING_IDEAS_MARKET,
    ROUTE_DISCOVER_SAMPLE_PORTFOLIOS,
    ROUTE_INVESTING_IDEA_MARKET,
    ROUTE_INVESTING_IDEA_MARKET_INDUSTRY,
    ROUTE_DEPRECATED_PUBLIC_PORTFOLIO,
    ROUTE_DEPRECATED_PORTFOLIO_SHARE,
    ROUTE_STOCKS,
    ROUTE_STOCKS_HOME,
    ROUTE_STOCKS_MARKET,
    ROUTE_STOCKS_MARKET_INDUSTRY,
    ROUTE_MARKET,
    ROUTE_MARKET_HOME,
    ROUTE_MARKET_SECTOR,
    ROUTE_MARKET_SECTOR_SECONDARY_TERTIARY,
    ROUTE_MARKET_SECTOR_SECONDARY,
  ],
  [ROUTE_WATCHLIST]: [ROUTE_WATCHLIST],
  [ROUTE_PORTFOLIO_LANDING]: [
    ROUTE_PORTFOLIO,
    ROUTE_PORTFOLIO_DEMO,
    ROUTE_PORTFOLIO_NEW,
    ROUTE_PORTFOLIO_DEFAULT,
    ROUTE_PORTFOLIO_LANDING,
    ROUTE_MY_PORTFOLIOS,
    ROUTE_PORTFOLIO_HOLDINGS,
    ROUTE_PORTFOLIO_ANALYSIS,
    ROUTE_PORTFOLIO_NARRATIVES,
    ROUTE_PORTFOLIO_DIVIDENDS,
    ROUTE_PORTFOLIO_RETURNS,
    ROUTE_DEPRECATED_PORTFOLIO,
    ROUTE_DEPRECATED_PORTFOLIO_EDIT,
    ROUTE_DEPRECATED_PORTFOLIO_HOME,
    ROUTE_DEPRECATED_PORTFOLIO_EDIT_TXN,
    ROUTE_DEPRECATED_PORTFOLIO_RETURNS_REPORT,
  ],
  [ROUTE_NARRATIVE_COMMUNITY]: [
    ROUTE_NARRATIVE_COMMUNITY,
    ROUTE_NARRATIVE_COMMUNITY_DETAIL,
    ROUTE_NARRATIVE_COMMUNITY_UPDATE_DETAIL,
    ROUTE_NARRATIVE_COMMUNITY_DETAIL_UPDATES,
    ROUTE_NARRATIVE_COMMUNITY_PROFILE,
  ],
  [ROUTE_CREATE_CRITERIA]: [
    ROUTE_CREATE_CRITERIA,
    ROUTE_EDIT_USER_IDEA,
    ROUTE_EDIT_EXPLORE_IDEA,
  ],
};

const useSelected = () => {
  const locationState = useSelector(getLocation);
  const isSelected = (currentRouteType: keyof typeof subRouteTypes) => {
    return subRouteTypes[currentRouteType]?.includes(locationState?.type);
  };
  return isSelected;
};

// const HighlighterBlip = ({ children }: { children: React.ReactNode }) => (
//   <RenderUntilExpiry startedFrom="2024-10-16" expiresIn={{ days: 14 }}>
//     <Badge tone="attention" size="small">
//       {children}
//     </Badge>
//   </RenderUntilExpiry>
// );
// <HighlighterBlip>{t('app.topbar.nav.badge.new')}</HighlighterBlip>;

const getNavItemClasses = (selected: boolean) =>
  cn(
    'h-x8 whitespace-nowrap text-xs leading-[calc(theme(spacing.x8)-2px)] text-softer [-webkit-tap-highlight-color:transparent] hover:text-solid focus:text-solid',
    selected && 'text-solid',
    'relative inline-block px-x1_25 transition-colors',
    '[&>span]:relative [&>span]:z-1',
    "after:absolute after:inset-[13px_1px_15px_1px] after:block after:rounded-md after:bg-transparent after:transition-colors after:content-[''] hover:after:bg-legacy-spacegray focus-visible:after:bg-legacy-spacegray [&:active:after]:opacity-70"
  );

export function Nav() {
  const { trackEvent } = useTracking();
  const isSelected = useSelected();
  const t = useTranslation();

  const navItems = {
    dashboard: (
      <RouterLink
        to={{ type: ROUTE_DASHBOARD }}
        noUnderline
        className={getNavItemClasses(isSelected(ROUTE_DASHBOARD))}
        onClick={() => {
          if (!isSelected(ROUTE_DASHBOARD)) {
            trackEvent({
              modifier: 'Dashboard',
              action: 'click',
            });
          }
        }}
      >
        <span>{t('app.topbar.nav.dashboard')}</span>
      </RouterLink>
    ),
    discover: (
      <RouterLink
        to={{ type: ROUTE_DISCOVER_INVESTING_IDEAS }}
        noUnderline
        className={getNavItemClasses(
          isSelected(ROUTE_DISCOVER_INVESTING_IDEAS)
        )}
        onClick={() => {
          if (!isSelected(ROUTE_DISCOVER_INVESTING_IDEAS)) {
            trackEvent({
              modifier: 'Discover',
              action: 'click',
            });
          }
        }}
      >
        <span>{t('app.topbar.nav.discover')}</span>
      </RouterLink>
    ),
    watchlist: (
      <RouterLink
        to={{ type: ROUTE_WATCHLIST }}
        noUnderline
        className={getNavItemClasses(isSelected(ROUTE_WATCHLIST))}
        onClick={() => {
          if (!isSelected(ROUTE_WATCHLIST)) {
            trackEvent({
              modifier: 'Watchlist',
              action: 'click',
            });
          }
        }}
      >
        <span>{t('app.topbar.nav.watchlist')}</span>
      </RouterLink>
    ),
    community: (
      <RouterLink
        to={{ type: ROUTE_NARRATIVE_COMMUNITY }}
        noUnderline
        className={getNavItemClasses(isSelected(ROUTE_NARRATIVE_COMMUNITY))}
        onClick={() => {
          if (!isSelected(ROUTE_NARRATIVE_COMMUNITY)) {
            trackEvent({
              modifier: 'Community',
              action: 'click',
            });
          }
        }}
      >
        <span>{t('app.topbar.nav.community')}</span>
      </RouterLink>
    ),
    portfolios: (
      <RouterLink
        to={{ type: ROUTE_PORTFOLIO_LANDING }}
        noUnderline
        className={getNavItemClasses(isSelected(ROUTE_PORTFOLIO_LANDING))}
        onClick={() => {
          if (!isSelected(ROUTE_PORTFOLIO_LANDING)) {
            trackEvent({
              modifier: 'Portfolios',
              action: 'click',
            });
          }
        }}
      >
        <span>{t('app.topbar.nav.portfolios')}</span>
      </RouterLink>
    ),
    screener: (
      <RouterLink
        to={{ type: ROUTE_CREATE_CRITERIA }}
        noUnderline
        className={getNavItemClasses(isSelected(ROUTE_CREATE_CRITERIA))}
        onClick={() => {
          if (!isSelected(ROUTE_CREATE_CRITERIA)) {
            trackEvent({
              modifier: 'Screener',
              action: 'click',
            });
          }
        }}
      >
        <span className="inline-grid grid-cols-[max-content_max-content] items-center gap-x0_5">
          {t('app.topbar.nav.screener')}
        </span>
      </RouterLink>
    ),
  } as const;

  return (
    <>
      {(
        [
          'dashboard',
          'portfolios',
          'watchlist',
          'community',
          'discover',
          'screener',
        ] satisfies (keyof typeof navItems)[]
      )
        .filter(Boolean)
        .map((key) => (
          <Fragment key={key}>{navItems[key]}</Fragment>
        ))}
    </>
  );
}
